import { clsx } from 'clsx';

interface ProgressSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Stroke width in pixels */
  strokeWidth?: number;
  /** Progress value for determinate spinner (0-100) */
  value?: number;
  /** Additional CSS classes */
  className?: string;
  /** Text to display below spinner */
  text?: string;
  /** Color variant */
  variant?: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
}

export function ProgressSpinner({
  size = 'md',
  strokeWidth = 2,
  value,
  className,
  text,
  variant = 'primary',
}: ProgressSpinnerProps) {
  const sizeMap = {
    sm: 16,
    md: 24,
    lg: 32,
    xl: 48,
  };

  const radius = sizeMap[size] / 2 - strokeWidth;
  const circumference = 2 * Math.PI * radius;
  const isIndeterminate = value === undefined;

  const colorClasses = {
    primary: 'text-primary-600 dark:text-primary-500',
    secondary: 'text-gray-500 dark:text-gray-400',
    success: 'text-green-600 dark:text-green-500',
    error: 'text-red-600 dark:text-red-500',
    warning: 'text-amber-600 dark:text-amber-500',
  };

  const backgroundColorClasses = {
    primary: 'text-primary-200 dark:text-primary-900',
    secondary: 'text-gray-200 dark:text-gray-700',
    success: 'text-green-200 dark:text-green-900',
    error: 'text-red-200 dark:text-red-900',
    warning: 'text-amber-200 dark:text-amber-900',
  };

  return (
    <div className={clsx('flex items-center justify-center', className)}>
      <div className="flex flex-col items-center gap-2">
        <div className="relative">
          {isIndeterminate ? (
            // Indeterminate spinner - simple spinning arc
            <svg width={sizeMap[size]} height={sizeMap[size]} className="animate-spin">
              {/* Background circle */}
              <circle
                cx={sizeMap[size] / 2}
                cy={sizeMap[size] / 2}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                className={backgroundColorClasses[variant]}
              />

              {/* Spinning arc */}
              <circle
                cx={sizeMap[size] / 2}
                cy={sizeMap[size] / 2}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className={colorClasses[variant]}
                style={{
                  strokeDasharray: `${circumference * 0.25} ${circumference * 0.75}`,
                }}
              />
            </svg>
          ) : (
            // Determinate spinner - progress circle
            <svg width={sizeMap[size]} height={sizeMap[size]}>
              {/* Background circle */}
              <circle
                cx={sizeMap[size] / 2}
                cy={sizeMap[size] / 2}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                className={backgroundColorClasses[variant]}
              />

              {/* Progress circle */}
              <circle
                cx={sizeMap[size] / 2}
                cy={sizeMap[size] / 2}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className={colorClasses[variant]}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: circumference - (circumference * (value || 0)) / 100,
                  transition: 'stroke-dashoffset 0.3s ease',
                  transform: 'rotate(-90deg)',
                  transformOrigin: `${sizeMap[size] / 2}px ${sizeMap[size] / 2}px`,
                }}
              />
            </svg>
          )}

          {/* Value text for determinate spinner */}
          {!isIndeterminate && value !== undefined && size !== 'sm' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {Math.round(value)}%
              </span>
            </div>
          )}
        </div>

        {text && <p className="text-sm text-gray-500 dark:text-gray-400">{text}</p>}
      </div>
    </div>
  );
}
